import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  AnalysisSelect,
  AnalysisSubHeader,
  TrendDataTable,
  useTrendChartProps,
} from '../../../../components';
import { formatMonth } from '../../../../utils/formatMonth';
import { brandColor } from '../../../../utils/brandColor';
import { CHART_TOOLTIP_STYLE } from '../../../../utils/chartTooltipStyle';
import type { MediaMetric, TrendPoint } from '../../model/aggregate';
import { formatMetricValue, formatPercentMagnitude, formatSignedPercent } from '../../model/format';
import { carouselPosition } from '../../model/carousel';
import { FLAT_CHANGE_PERCENT, type TrendInsight } from '../../model/insights';
import { InsightCarousel } from '../InsightCarousel';

interface SpendTrendProps {
  points: TrendPoint[];
  advertisers: string[];
  orderedAdvertisers: string[];
  metric: MediaMetric;
  onMetricChange: (metric: MediaMetric) => void;
  insights: TrendInsight[];
}

const CHART_HEIGHT = 320;

export const SpendTrend = ({
  points,
  advertisers,
  orderedAdvertisers,
  metric,
  onMetricChange,
  insights,
}: SpendTrendProps) => {
  const { t } = useTranslation('analysis');
  const [index, setIndex] = useState(0);

  const titleByMetric: Record<MediaMetric, string> = {
    spend: t('analysisResult.mediaLandscape.brandSpendOverTime'),
    impressions: t('analysisResult.mediaLandscape.brandImpressionsOverTime'),
    cpm: t('analysisResult.mediaLandscape.brandCpmOverTime'),
  };
  const metricNoun: Record<MediaMetric, string> = {
    spend: t('analysisResult.mediaLandscape.metricNounSpend'),
    impressions: t('analysisResult.mediaLandscape.metricNounImpressions'),
    cpm: t('analysisResult.mediaLandscape.metricNounCpm'),
  };
  const metricOptions: Array<{ value: MediaMetric; label: string }> = [
    { value: 'spend', label: t('analysisResult.mediaLandscape.totalSpend') },
    { value: 'impressions', label: t('analysisResult.mediaLandscape.impressions') },
    { value: 'cpm', label: t('analysisResult.mediaLandscape.avgCpm') },
  ];

  const current = insights[carouselPosition(index, insights.length)];
  const chartProps = useTrendChartProps(titleByMetric[metric], advertisers, points);

  const sentence = (insight: TrendInsight): string => {
    const shared = {
      brand: insight.advertiser,
      metricNoun: metricNoun[insight.metric],
      month: formatMonth(insight.month, 'numeric'),
    };
    if (insight.changePercent === null) {
      return t('analysisResult.mediaLandscape.trendInsightLevel', {
        ...shared,
        value: formatMetricValue(insight.value, insight.metric),
      });
    }
    const isFlat = Math.abs(insight.changePercent) < FLAT_CHANGE_PERCENT;
    const key = isFlat
      ? 'analysisResult.mediaLandscape.trendInsightFlat'
      : insight.changePercent > 0
        ? 'analysisResult.mediaLandscape.trendInsightRose'
        : 'analysisResult.mediaLandscape.trendInsightFell';
    const clauses: string[] = [
      t(key, { ...shared, change: formatPercentMagnitude(insight.changePercent) }),
    ];
    if (insight.divergence === 'efficiency') {
      clauses.push(t('analysisResult.mediaLandscape.trendInsightEfficiency'));
    }
    if (insight.divergence === 'costlier') {
      clauses.push(t('analysisResult.mediaLandscape.trendInsightCostlier'));
    }
    if (insight.topDimension) {
      clauses.push(
        t('analysisResult.mediaLandscape.trendInsightTopChannel', {
          channel: insight.topDimension,
        }),
      );
    }
    return clauses.join(' ');
  };

  return (
    <div className="flex flex-col gap-4">
      <AnalysisSubHeader
        title={titleByMetric[metric]}
        subtitle={t('analysisResult.mediaLandscape.trendSubtitle')}
        subtitleTone="meta"
      />
      <div className="flex flex-wrap items-center gap-2.5">
        <AnalysisSelect
          variant="filter"
          ariaLabel={t('analysisResult.mediaLandscape.metric')}
          value={metric}
          onValueChange={(next) => onMetricChange(next as MediaMetric)}
          options={metricOptions}
        />
      </div>
      <div className="flex flex-col items-stretch gap-5 rounded-xl border border-border-light bg-card p-4 lg:flex-row">
        <div className="min-w-0 flex-1">
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <LineChart
              data={points}
              margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
              {...chartProps}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="month"
                tickFormatter={(month: string) => formatMonth(month, '2-digit')}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                width={48}
                tickFormatter={(value: number) => formatMetricValue(value, metric)}
              />
              <Tooltip
                {...CHART_TOOLTIP_STYLE}
                isAnimationActive={false}
                labelFormatter={(month) =>
                  typeof month === 'string' ? formatMonth(month, 'numeric') : month
                }
                formatter={(value) =>
                  formatMetricValue(typeof value === 'number' ? value : 0, metric)
                }
              />
              <Legend />
              {advertisers.map((advertiser) => (
                <Line
                  key={advertiser}
                  type="monotone"
                  dataKey={advertiser}
                  stroke={brandColor(advertiser, orderedAdvertisers)}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
          <TrendDataTable
            caption={titleByMetric[metric]}
            series={advertisers}
            points={points}
            formatValue={(value) => formatMetricValue(value, metric)}
          />
        </div>
        <InsightCarousel
          title={t('analysisResult.mediaLandscape.keyInsight')}
          count={insights.length}
          index={index}
          onIndexChange={setIndex}
        >
          {insights.length > 0 && (
            <>
              <div className="flex items-center gap-2">
                <span className="text-lg font-medium text-foreground">{current.advertiser}</span>
                <span className="rounded bg-accent px-2 py-0.5 text-xs font-medium text-accent-foreground">
                  {formatMonth(current.month, 'numeric')}
                </span>
              </div>
              <p className="flex-1 text-pretty text-sm leading-5 text-foreground-alt">
                {sentence(current)}
              </p>
              <div className="flex flex-col gap-1.5 rounded-lg bg-card p-3">
                {current.changePercent !== null && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {t('analysisResult.mediaLandscape.change')}
                    </span>
                    <span
                      className={clsx(
                        'font-medium',
                        current.changePercent >= 0 ? 'text-chart-positive' : 'text-chart-negative',
                      )}
                    >
                      {formatSignedPercent(current.changePercent)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {t('analysisResult.mediaLandscape.value')}
                  </span>
                  <span className="font-medium">
                    {formatMetricValue(current.value, current.metric)}
                  </span>
                </div>
              </div>
            </>
          )}
        </InsightCarousel>
      </div>
    </div>
  );
};
